import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import formatPhone from '../utils/formatPhone';
import { prDaProposta } from '../utils/performanceRatio';
import { getUnoModulo, getUnoInversor, getIndices, getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

// ============================================================
// OPERIS PV — Proposta Comercial PDF
// Marcki Engenharia — Layout v2.0 (corrigido)
// ============================================================

const generatePdf = async (proposta) => {
  // Proteção contra proposta undefined
  if (!proposta) {
    console.error('generatePdf: proposta é undefined!');
    return;
  }

  const pdfDoc = await PDFDocument.create();

  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold    = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W  = 595.28;
  const H  = 841.89;
  const ML = 40;
  const MR = 40;
  const CW = W - ML - MR;

  // Paleta Marcki
  const C = {
    azul:       rgb(0.102, 0.227, 0.431),
    azulClaro:  rgb(0.910, 0.941, 0.969),
    azulMedio:  rgb(0.816, 0.847, 0.910),
    laranja:    rgbHex(coresHex.laranja),
    verde:      rgb(0.180, 0.490, 0.196),
    cinza:      rgb(0.412, 0.412, 0.412),
    cinzaClaro: rgb(0.961, 0.961, 0.961),
    bordaAzul:  rgb(0.690, 0.769, 0.847),
    preto:      rgb(0.133, 0.133, 0.133),
    branco:     rgb(1, 1, 1),
  };

  // ── Utilitários ──
  const cx = (text, size, font) => (W - font.widthOfTextAtSize(text, size)) / 2;

  const brl = (v) => parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const pct = (v) => `${(v * 100).toFixed(2)}%`;

  const drawRect = (page, x, y, w, h, color) =>
    page.drawRectangle({ x, y, width: w, height: h, color });

  // FIX: drawBorderedRect agora desenha o retângulo a partir do y como base superior da linha,
  // então o fundo da célula fica em y - tH e o topo em y + 2 (padding).
  const drawBorderedRect = (page, x, y, w, h, fill, borderColor) =>
    page.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor, borderWidth: 0.5 });

  const drawText = (page, text, x, y, size, font, color) =>
    page.drawText(String(text), { x, y, size, font, color });

  const drawLine = (page, x1, y1, x2, y2, color = C.bordaAzul, thickness = 0.5) =>
    page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness, color });

  const drawSectionHeader = (page, label, y, bgColor = C.azul) => {
    drawRect(page, ML, y - 4, CW, 18, bgColor);
    drawText(page, label, ML + 6, y, 10, fontBold, C.branco);
    return y - 22;
  };

  // ── Função de linha de tabela (CORRIGIDA) ──
  // Cada linha tem altura tH. O retângulo é desenhado de (y - tH + 2) com altura tH.
  // O texto é desenhado em (y - tH + 2 + 3) = y - tH + 5 para ficar centralizado na célula.
  const tH = 14;
  const drawRow = (page, cells, y) => {
    // cells: [{x, w, fill, borderColor, text, font, color, size, align}]
    cells.forEach((cell) => {
      drawBorderedRect(page, cell.x, y - tH + 2, cell.w, tH, cell.fill, cell.borderColor || C.bordaAzul);
      if (cell.text !== undefined && cell.text !== null && cell.text !== '') {
        const sz = cell.size || 8;
        const fn = cell.font || fontRegular;
        let tx = cell.x + 3;
        if (cell.align === 'right') {
          tx = cell.x + cell.w - fn.widthOfTextAtSize(String(cell.text), sz) - 3;
        } else if (cell.align === 'center') {
          tx = cell.x + (cell.w - fn.widthOfTextAtSize(String(cell.text), sz)) / 2;
        }
        drawText(page, String(cell.text), tx, y - tH + 5, sz, fn, cell.color || C.preto);
      }
    });
    return y - tH;
  };

  // Converte string base64 (data:image/jpeg;base64,XXX) em Uint8Array
  const base64ToBytes = (base64Str) => {
    const part = base64Str.split(',')[1];
    const bin  = atob(part);
    const arr  = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
    return arr;
  };

  // Embeds base64 como PNG ou JPG automaticamente
  const embedBase64 = async (doc, base64Str) => {
    const bytes = base64ToBytes(base64Str);
    if (base64Str.includes('image/png')) return doc.embedPng(bytes);
    return doc.embedJpg(bytes);
  };

  // ── Imagens (logo da empresa configurada — antes fixa em /logo.png / /logoweg.png) ──
  const empresa = await getEmpresaAtual();
  let logoImg = null;
  if (empresa?.logoPrincipal) {
    try { logoImg = await embedBase64(pdfDoc, empresa.logoPrincipal); }
    catch (e) { console.warn('Logo principal não carregada:', e); }
  }

  let logoWegImg = null;
  if (empresa?.logoParceiro) {
    try { logoWegImg = await embedBase64(pdfDoc, empresa.logoParceiro); }
    catch (e) { console.warn('Logo de parceiro não carregada:', e); }
  }

  // Módulo — foto salva como base64 no banco
  let moduloImg = null;
  try {
    if (proposta.kit?.modulo) {
      const modulo = await getUnoModulo(proposta.kit.modulo);
      if (modulo?.foto) moduloImg = await embedBase64(pdfDoc, modulo.foto);
    }
  } catch (e) { console.warn('Imagem módulo não carregada:', e); }

  // Inversor — foto salva como base64 no banco
  let inversorImg = null;
  try {
    if (proposta.kit?.inversor) {
      const inversor = await getUnoInversor(proposta.kit.inversor);
      if (inversor?.foto) inversorImg = await embedBase64(pdfDoc, inversor.foto);
    }
  } catch (e) { console.warn('Imagem inversor não carregada:', e); }

  // Estrutura — arquivo estático em /public/produtos/
  let estruturaImg = null;
  try {
    const b = await fetch('/produtos/estrutura.png').then((r) => r.arrayBuffer());
    estruturaImg = await pdfDoc.embedPng(b);
  } catch (e) { console.warn('Imagem estrutura não carregada:', e); }

  // ── Índices ──
  const indices = await getIndices();

  // FIX: Tarifa atual vinda da proposta (campo digitado pelo usuário) ou fallback do índice
  const indiceTarifaCemig = proposta.tarifaEnergia
    ? parseFloat(proposta.tarifaEnergia)
    : (indices.find((i) => i.nome === 'Tarifa CEMIG Atual')?.valor || 0.99);

  // FIX: Distribuidora vinda da proposta
  const nomeDistribuidora = proposta.distribuidora || 'CEMIG';

  const historicoAnos   = [2016,2017,2018,2019,2020,2021,2022,2023,2024,2025];
  const historicoTarifa = historicoAnos.map((ano) =>
    indices.find((i) => i.nome === `Tarifa CEMIG ${ano}`)?.valor || 0
  );

  const anoAtual = new Date().getFullYear();
  // FIX: anos5 em ordem crescente e pulando o ano atual se não houver tarifa cadastrada
  const anos5ultimos = [anoAtual-4, anoAtual-3, anoAtual-2, anoAtual-1, anoAtual];
  const tarifa5 = anos5ultimos.map((ano) => {
    if (ano === anoAtual) return indiceTarifaCemig; // usa tarifa atual informada
    return indices.find((i) => i.nome === `Tarifa CEMIG ${ano}`)?.valor || 0;
  });

  // ── Cálculos base ──
  const consumoMedio  = proposta.mediaconsumo || 0;
  const consumoAnual  = consumoMedio * 12;
  const tarifaCemig   = indiceTarifaCemig;
  const valorMensal   = consumoMedio * tarifaCemig;
  const valorAnual    = valorMensal * 12;

  // ── Nova metodologia — HSP por cidade + PR + Fator Orientação ──
  const hspData         = proposta.hsp || null;
  const pr              = prDaProposta(proposta);
  const fatorOrientacao = proposta.fatorOrientacao || 1.0;
  const potCC           = proposta.kit?.potenciacc || 0;

  // Proteção para propostas antigas sem kit definido
  if (!proposta.kit) {
    console.warn('PDF: proposta sem kit definido, alguns campos podem estar vazios.');
  }

  // HSP mensais da cidade (fallback para valores médios de Araguari/MG)
  const HSP_KEYS = ['hspJan','hspFev','hspMar','hspAbr','hspMai','hspJun',
                    'hspJul','hspAgo','hspSet','hspOut','hspNov','hspDez'];
  const hspMes = hspData
    ? HSP_KEYS.map((k) => hspData[k] || 0)
    : [5.873,6.139,5.702,5.413,4.890,4.661,4.972,5.630,5.972,5.937,5.913,5.858];

  const hspMedia = hspData?.hspMedia || (hspMes.reduce((a, b) => a + b, 0) / 12);

  // Geração mensal: PotCC × HSP_mês × 30 × PR × FatorOrientação
  const geracaoMes  = hspMes.map((h) => Math.round(potCC * h * 30 * pr * fatorOrientacao));
  const mesesAbrev  = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

  // Geração e economia médias mensais
  const geracaoMediaMensal = Math.round(geracaoMes.reduce((a, b) => a + b, 0) / 12);
  const tarifaGeracao      = tarifaCemig * 0.8839;
  const economiaMes        = geracaoMes.map((g) => g * tarifaGeracao);
  const economiaMensal     = geracaoMediaMensal * tarifaGeracao;
  const totalCemig         = geracaoMediaMensal * tarifaCemig;
  const totalGeracao       = geracaoMediaMensal * tarifaGeracao;
  const economiaPct        = totalCemig > 0 ? totalGeracao / totalCemig : 0;

  // ── Dados físicos do kit ──
  const qtdModulos    = proposta.kit?.quantidademodulos || 0;
  const potModulo     = proposta.kit?.modulo?.potencia || 0;
  const potTotalKwp   = (qtdModulos * potModulo) / 1000;
  const areaOcupada   = (qtdModulos * 2.75).toFixed(2);
  const pesoUnitario  = proposta.kit?.modulo?.peso || 0;
  const cargaTotal    = parseFloat(areaOcupada) > 0
    ? ((pesoUnitario * qtdModulos) / parseFloat(areaOcupada)).toFixed(2) : 0;
  const qtdInversores = proposta.kit?.quantidadeinversores || 0;
  const potInversor   = proposta.kit?.inversor?.potencia || 0;
  const potTotalKw    = potInversor * qtdInversores;
  const pesoTotal     = pesoUnitario * qtdModulos;
  const valorProjeto  = parseFloat(proposta.valor) || 0;

  // ── Depreciação do módulo ──
  const deprecPrimeiro = parseFloat(proposta.kit?.modulo?.depreciacaoprimeiro || 2.0) / 100;
  const deprecDemais   = parseFloat(proposta.kit?.modulo?.depreciacaodemais   || 0.7)  / 100;

  // ── Financeiro ──
  const calcParcela = (valor, n) => {
    const taxa = 0.0185;
    const iof  = 999;
    const cad  = valor * 0.034;
    return (valor + iof + cad) * (taxa * ((1 + taxa) ** n)) / (((1 + taxa) ** n) - 1);
  };

  const paybackProprio       = economiaMensal > 0 ? valorProjeto / (economiaMensal * 12) : 0;
  const parcela36            = calcParcela(valorProjeto, 36);
  const paybackFinanciamento = economiaMensal > 0 ? (parcela36 * 36) / (economiaMensal * 12) : 0;
  const roi                  = valorProjeto > 0 ? (economiaMensal * 30 * 12) / valorProjeto : 0;
  const retornoMensal        = valorProjeto > 0 ? economiaMensal / valorProjeto : 0;

  // ── Análise financeira 5 anos ──
  const analise = Array.from({ length: 5 }, (_, i) => {
    const tarifaGer   = tarifaGeracao * (1.05 ** i);
    const tarifaC     = tarifaCemig   * (1.05 ** i);
    const econAnual   = consumoAnual  * tarifaGer;
    const faturaCemig = consumoAnual  * tarifaC;
    return {
      ano: anoAtual + i,
      consumo: consumoAnual,
      tarifa: tarifaGer,
      econAnual,
      econPct: faturaCemig > 0 ? econAnual / faturaCemig : 0,
    };
  });

  // ============================================================
  // PÁGINA 1 — CAPA
  // ============================================================
  const p1 = pdfDoc.addPage([W, H]);

  let logoDims = { width: 0, height: 0 };
  if (logoImg) {
    logoDims = logoImg.scale(0.22);
    p1.drawImage(logoImg, {
      x: (W - logoDims.width) / 2,
      y: H - logoDims.height - 28,
      width: logoDims.width,
      height: logoDims.height,
    });
  }

  if (logoWegImg) {
    const wd = logoWegImg.scale(0.5);
    p1.drawImage(logoWegImg, {
      x: (W - wd.width) / 2,
      y: H - logoDims.height - wd.height - 48,
      width: wd.width,
      height: wd.height,
    });
  }

  const titulo = 'Proposta Comercial Sistema Fotovoltaico';
  drawText(p1, titulo, cx(titulo, 16, fontBold), H - 340, 16, fontBold, C.azul);

  let dy = H - 390;
  const labelX = ML + 30;
  const valorX  = labelX + 130;
  const lh      = 22;

  const drawDado = (label, valor, y) => {
    drawText(p1, label, labelX, y, 10, fontBold, C.azul);
    drawText(p1, valor || '—', valorX, y, 10, fontRegular, C.preto);
  };

  // FIX: Nosso número e Pós-vendas com valores reais
  drawDado('Cliente:',      proposta.cliente?.nome || '', dy); dy -= lh;
  drawDado('Telefone:',     formatPhone(proposta.cliente?.telefone || ''), dy); dy -= lh;
  drawDado('Data:',         new Date().toLocaleDateString('pt-BR'), dy); dy -= lh;
  drawDado('Consultor:',    proposta.user?.nome || '', dy); dy -= lh;
  drawDado('Contato:',      formatPhone(proposta.user?.telefone || ''), dy); dy -= lh * 1.5;
  drawDado('Nosso número:', empresa?.telefone || '(34) 99925-5093', dy); dy -= lh;
  drawDado('Pós-vendas:',   empresa?.telefone || '(34) 98887-5093', dy);

  const nomeEmp = empresa?.nome || 'MARCKI Engenharia';
  const enderecoEmp = empresa?.enderecoRodape || 'Araguari/MG';
  drawText(p1, nomeEmp, cx(nomeEmp, 11, fontBold), 70, 11, fontBold, C.azul);
  drawText(p1, enderecoEmp, cx(enderecoEmp, 10, fontRegular), 55, 10, fontRegular, C.azul);

  // ============================================================
  // PÁGINA 2 — ANALISANDO SUA FATURA
  // ============================================================
  const p2 = pdfDoc.addPage([W, H]);
  let y2 = H - 30;

  y2 = drawSectionHeader(p2, '1 | Analisando sua Fatura de Energia', y2, C.laranja);
  y2 -= 6;

  const intro = [
    'Nossa empresa busca atender, de forma otimizada e personalizada, a demanda energética de cada cliente,',
    'pois cada projeto é único em sua forma de instalação, tecnologia mais adequada, forma de pagamento,',
    'porte do sistema. Enfim, são várias as características que afetam tanto a performance do seu sistema',
    'quanto o retorno de seu investimento. A seguir, apresentamos um resumo do seu consumo de energia.',
  ];
  intro.forEach((l) => { drawText(p2, l, ML, y2, 8, fontBold, C.preto); y2 -= 11; });
  y2 -= 6;

  // ── Tabela consumo (CORRIGIDA com drawRow) ──
  const c1W = CW * 0.52;
  const c2W = CW * 0.10;
  const c3W = CW * 0.24;
  const c4W = CW * 0.14;

  const consumoRows = [
    ['Consumo Médio dos últimos 12 meses:', '', `${consumoMedio}`, 'kWh/mês'],
    ['Valor da Tarifa de Energia:', 'R$', tarifaCemig.toLocaleString('pt-BR',{minimumFractionDigits:2}), '/kWh'],
    ['Valor médio mensal da Fatura de Energia:', 'R$', valorMensal.toLocaleString('pt-BR',{minimumFractionDigits:2}), '/mês'],
    ['Valor anual da Fatura de Energia:', 'R$', valorAnual.toLocaleString('pt-BR',{minimumFractionDigits:2}), '/ano'],
  ];
  consumoRows.forEach(([label, rs, valor, unidade]) => {
    y2 = drawRow(p2, [
      { x: ML,              w: c1W, fill: C.azulClaro, text: label,          font: fontRegular, color: C.preto   },
      { x: ML+c1W,          w: c2W, fill: C.azulClaro, text: rs,             font: fontBold,    color: C.laranja },
      { x: ML+c1W+c2W,      w: c3W, fill: C.azulClaro, text: valor,          font: fontBold,    color: C.laranja, align: 'right' },
      { x: ML+c1W+c2W+c3W,  w: c4W, fill: C.azulClaro, text: unidade,        font: fontRegular, color: C.preto   },
    ], y2);
  });
  y2 -= 4;

  // Cidade/Estado
  const cid1 = CW * 0.28; const cid2 = CW * 0.22;
  const est1  = CW * 0.25; const est2  = CW * 0.25;
  y2 = drawRow(p2, [
    { x: ML,             w: cid1, fill: C.branco, text: 'Cidade de Instalação:', font: fontRegular, color: C.preto },
    { x: ML+cid1,        w: cid2, fill: C.branco, text: proposta.localinstalacao?.cidade?.nome || '', font: fontRegular, color: C.preto },
    { x: ML+cid1+cid2,   w: est1, fill: C.branco, text: 'Estado:',               font: fontRegular, color: C.preto },
    { x: ML+cid1+cid2+est1, w: est2, fill: C.branco, text: proposta.localinstalacao?.estado?.sigla || '', font: fontRegular, color: C.preto },
  ], y2);
  y2 -= 6;

  // Header tabela 5 anos
  drawBorderedRect(p2, ML, y2-tH+2, CW, tH, C.azul, C.bordaAzul);
  const pergunta = 'Uma pergunta importante? Você já parou para pensar quanto já pagou de energia elétrica nos últimos 5 anos? O que você faria se pudesse economizar esta quantia?';
  drawText(p2, pergunta, ML+3, y2-tH+5, 6.5, fontBold, C.branco);
  y2 -= tH;

  // Cabeçalho tabela 5 anos
  const c5 = [CW*0.10, CW*0.22, CW*0.20, CW*0.28, CW*0.20];
  const c5x = [ML, ML+c5[0], ML+c5[0]+c5[1], ML+c5[0]+c5[1]+c5[2], ML+c5[0]+c5[1]+c5[2]+c5[3]];
  y2 = drawRow(p2, ['Ano','Consumo Anual','Valor do kWh','Valor Pago','Reajuste'].map((h, i) => ({
    x: c5x[i], w: c5[i], fill: C.azulMedio, text: h, font: fontBold, color: C.preto,
  })), y2);

  // FIX: anos em ordem crescente, 2026 com valor real
  anos5ultimos.forEach((ano, idx) => {
    const t = tarifa5[idx];
    const vp = consumoAnual * t;
    let raj = '-';
    if (idx < anos5ultimos.length - 1) {
      const tProx = tarifa5[idx + 1];
      raj = tProx > 0 ? `${((tProx - t) / t * 100).toFixed(2)}%` : '-';
    }
    const fill = idx % 2 === 0 ? C.branco : C.cinzaClaro;
    y2 = drawRow(p2, [
      { x: c5x[0], w: c5[0], fill, text: String(ano),            font: fontBold,    color: C.preto   },
      { x: c5x[1], w: c5[1], fill, text: String(consumoAnual),   font: fontRegular, color: C.preto   },
      { x: c5x[2], w: c5[2], fill, text: `R$ ${t.toFixed(2)}`,   font: fontRegular, color: C.preto   },
      { x: c5x[3], w: c5[3], fill, text: brl(vp),                font: fontBold,    color: C.laranja },
      { x: c5x[4], w: c5[4], fill, text: raj,                    font: fontRegular, color: C.preto   },
    ], y2);
  });
  y2 -= 6;

  // Bloco "não para de subir"
  drawRect(p2, ML, y2-tH+2, CW, tH, C.laranja);
  const tNsobe = 'Uma conta que não para de subir...';
  drawText(p2, tNsobe, cx(tNsobe, 9, fontBold), y2-tH+5, 9, fontBold, C.branco);
  y2 -= tH + 4;

  const textoNsobe = [
    'Além do aumento natural do consumo de energia elétrica ocasionado pelo crescimento tecnológico',
    'como a aquisição de aparelhos de ar-condicionado, fogão a indução, piscina aquecida com trocador de calor,',
    'aquisição de carros elétricos e híbridos e afins, a própria tarifa de energia é ajustada anualmente e,',
    'na grande maioria dos casos, em valores que superam e muito a própria inflação.',
    'A seguir, mostramos a evolução da tarifa de energia dos últimos 10 anos:',
  ];
  textoNsobe.forEach((l, i) => {
    const bold = i >= 3;
    drawText(p2, l, cx(l, 8, bold ? fontBold : fontRegular), y2, 8, bold ? fontBold : fontRegular, C.preto);
    y2 -= 11;
  });
  y2 -= 6;

  // Título gráfico
  const tGrafico = `Tarifa Média ${nomeDistribuidora}`;
  drawText(p2, tGrafico, cx(tGrafico, 10, fontBold), y2, 10, fontBold, C.preto);
  y2 -= 16;

  // FIX: Gráfico maior ocupando o restante da página
  const gX   = ML + 42;
  const gW   = CW - 46;
  const gY   = 36;                   // base do gráfico perto do fim da página
  const gH   = y2 - gY - 20;        // altura dinâmica — ocupa o espaço disponível
  const maxT = Math.max(...historicoTarifa) * 1.10;
  const nBars = historicoAnos.length;
  const barSlot = gW / nBars;
  const barW    = barSlot * 0.55;

  // Eixos
  drawLine(p2, gX, gY, gX, gY+gH, C.cinza, 0.8);
  drawLine(p2, gX, gY, gX+gW, gY, C.cinza, 0.8);

  // Linhas de grade e eixo Y
  const nLinhas = 9;
  for (let i = 0; i <= nLinhas; i += 1) {
    const val = maxT * i / nLinhas;
    const yp  = gY + (gH * i / nLinhas);
    drawLine(p2, gX, yp, gX+gW, yp, rgb(0.85,0.85,0.85), 0.3);
    const label = `R$${val.toFixed(2)}`;
    drawText(p2, label, ML, yp - 3, 7, fontRegular, C.cinza);
  }

  // Barras + rótulos
  historicoAnos.forEach((ano, i) => {
    const t   = historicoTarifa[i];
    const bH  = (t / maxT) * gH;
    const bX  = gX + i * barSlot + (barSlot - barW) / 2;

    drawRect(p2, bX, gY, barW, bH, rgb(0.267, 0.447, 0.769));

    // Rótulo dentro do topo da barra, branco, centralizado horizontalmente
    const rotulo = `R$${t.toFixed(2)}`;
    const rSz = 6;
    const rW  = fontBold.widthOfTextAtSize(rotulo, rSz);
    if (bH > 14) {
      drawText(p2, rotulo, bX + (barW - rW) / 2, gY + bH - 11, rSz, fontBold, C.branco);
    } else {
      // barra muito baixa: coloca acima em azul
      drawText(p2, rotulo, bX + (barW - rW) / 2, gY + bH + 2, rSz, fontBold, C.azul);
    }

    // Ano no eixo X centralizado, bold azul
    const anoStr = String(ano);
    const aW = fontBold.widthOfTextAtSize(anoStr, 7.5);
    drawText(p2, anoStr, bX + (barW - aW) / 2, gY - 13, 7.5, fontBold, C.azul);
  });

  // ============================================================
  // PÁGINA 3 — SOLUÇÃO PROPOSTA
  // ============================================================
  const p3 = pdfDoc.addPage([W, H]);
  let y3 = H - 30;

  y3 = drawSectionHeader(p3, '2 | Solução Proposta', y3, C.laranja);
  y3 -= 6;

  [
    'Para atender a demanda de energia existente na instalação e representada na página anterior, analisamos',
    'diversos fatores para deixar sua proposta personalizada, tais como: padrão de energia, área disponível',
    'para instalação e orientação do telhado existente. Todo este cuidado é tomado para que possamos',
    'entregar uma solução para você e não mais uma dor de cabeça.',
  ].forEach((l) => { drawText(p3, l, ML, y3, 8, fontBold, C.preto); y3 -= 11; });
  y3 -= 6;

  // Resumo sistema
  drawBorderedRect(p3, ML, y3-tH+2, CW, tH, C.azul, C.bordaAzul);
  drawText(p3, 'Resumo do sistema:', cx('Resumo do sistema:', 9, fontBold), y3-tH+5, 9, fontBold, C.branco);
  y3 -= tH;

  const colS = CW / 3;
  const drawSisRow = (d1, v1, d2, v2, d3, v3, y) => {
    [[d1,v1],[d2,v2],[d3,v3]].forEach(([d,v], i) => {
      const bx = ML + i * colS;
      drawBorderedRect(p3, bx,             y-tH+2, colS*0.55, tH, C.azulClaro, C.bordaAzul);
      drawBorderedRect(p3, bx+colS*0.55,   y-tH+2, colS*0.45, tH, C.azulClaro, C.bordaAzul);
      drawText(p3, d, bx+3,           y-tH+5, 7.5, fontRegular, C.preto);
      drawText(p3, String(v), bx+colS*0.55+3, y-tH+5, 7.5, fontBold, C.preto);
    });
    return y - tH;
  };

  y3 = drawSisRow('Quantidade de módulos:', qtdModulos,    'Potência Individual:', potModulo,             'Potência Total (kWp):', potTotalKwp.toFixed(1), y3);
  y3 = drawSisRow('Área Ocupada (m²):',     areaOcupada,  'Peso Unitário (kg):',  pesoUnitario,          'Carga Total (kg/m²):',  cargaTotal,             y3);
  y3 = drawSisRow('Qtd. de inversores:',    qtdInversores,'Potência Individual:',  potInversor,           'Potência Total (kW):',  potTotalKw,             y3);
  y3 -= 6;

  // Resumo geração
  drawBorderedRect(p3, ML, y3-tH+2, CW, tH, C.azul, C.bordaAzul);
  drawText(p3, 'Resumo da sua Geração:', cx('Resumo da sua Geração:', 9, fontBold), y3-tH+5, 9, fontBold, C.branco);
  y3 -= tH;

  // FIX: Tabela de geração reestruturada sem R$ duplicado
  const mmW = CW * 0.20; const midLW = CW * 0.18;
  const midVW = CW * 0.14; const totLW = CW * 0.22; const totVW = CW * 0.26;

  const gerRows = [
    {
      col1: `Média Mensal (kWh/mês): ${geracaoMediaMensal}`,
      col2l: 'Tarifa CEMIG:',      col2v: `R$ ${tarifaCemig.toFixed(2)}`,
      col3l: 'Total CEMIG:',       col3v: brl(totalCemig),   bold3: false,
    },
    {
      col1: '',
      col2l: 'Tarifa Geração:',    col2v: `R$ ${tarifaGeracao.toFixed(2)}`,
      col3l: 'Total Geração:',     col3v: brl(totalGeracao), bold3: false,
    },
    {
      col1: '',
      col2l: 'Economia:',          col2v: '',
      col3l: brl(economiaMensal),  col3v: pct(economiaPct),  bold3: true,
    },
  ];

  gerRows.forEach((row) => {
    y3 = drawRow(p3, [
      { x: ML,                          w: mmW,   fill: C.azulClaro, text: row.col1,  font: fontBold, color: C.preto   },
      { x: ML+mmW,                      w: midLW, fill: C.azulClaro, text: row.col2l, font: fontRegular, color: C.preto },
      { x: ML+mmW+midLW,                w: midVW, fill: C.azulClaro, text: row.col2v, font: fontRegular, color: C.preto },
      { x: ML+mmW+midLW+midVW,          w: totLW, fill: C.azulClaro, text: row.col3l, font: row.bold3 ? fontBold : fontRegular, color: row.bold3 ? C.verde : C.preto },
      { x: ML+mmW+midLW+midVW+totLW,    w: totVW, fill: C.azulClaro, text: row.col3v, font: fontBold, color: row.bold3 ? C.laranja : C.preto },
    ], y3);
  });
  y3 -= 6;

  // Detalhamento geração
  drawBorderedRect(p3, ML, y3-tH+2, CW, tH, C.azul, C.bordaAzul);
  drawText(p3, 'Detalhamento da sua Geração:', cx('Detalhamento da sua Geração:', 9, fontBold), y3-tH+5, 9, fontBold, C.branco);
  y3 -= tH + 6;

  // Gráfico geração: reserva espaço para legenda (20) + disclaimer (52) + meses (16) + margem (10)
  const gGX       = ML + 32;
  const gGW       = CW - 36;
  const discH     = 52;   // altura do bloco disclaimer
  const legH      = 22;   // altura da legenda
  const eixoXH    = 16;   // altura dos meses abaixo do eixo X
  const margemInf = 10;
  const gGY       = margemInf + discH + legH + eixoXH;   // base do gráfico
  const gGH       = y3 - gGY - 16;                       // altura disponível
  const maxGer = Math.max(...geracaoMes) * 1.18;
  const slotG  = gGW / 12;
  const bGW    = slotG * 0.38;

  drawText(p3, 'Geração Mensal', cx('Geração Mensal', 10, fontBold), y3 + 2, 10, fontBold, C.preto);

  // Eixos
  drawLine(p3, gGX, gGY, gGX, gGY+gGH, C.cinza, 0.8);
  drawLine(p3, gGX, gGY, gGX+gGW, gGY, C.cinza, 0.8);

  // Grade e eixo Y
  for (let i = 0; i <= 5; i += 1) {
    const v  = Math.round(maxGer * i / 5);
    const yp = gGY + (gGH * i / 5);
    drawLine(p3, gGX, yp, gGX+gGW, yp, rgb(0.85,0.85,0.85), 0.3);
    drawText(p3, String(v), ML, yp - 3, 7, fontRegular, C.cinza);
  }

  geracaoMes.forEach((g, i) => {
    const eco  = economiaMes[i];
    const bX   = gGX + i * slotG + (slotG - bGW * 2 - 2) / 2;

    // Barra geração
    const bHg  = (g / maxGer) * gGH;
    drawRect(p3, bX,        gGY, bGW, bHg, rgb(0.267, 0.447, 0.769));

    // Barra economia
    const bHe  = (eco / maxGer) * gGH;
    drawRect(p3, bX+bGW+2, gGY, bGW, bHe, rgb(0.439, 0.678, 0.278));

    // Rótulos dentro das barras em branco, centralizados
    const gStr = String(g);
    const gSz  = 6;
    const gW2  = fontBold.widthOfTextAtSize(gStr, gSz);
    if (bHg > 14) {
      drawText(p3, gStr, bX + (bGW - gW2) / 2, gGY + bHg - 11, gSz, fontBold, C.branco);
    } else {
      drawText(p3, gStr, bX + (bGW - gW2) / 2, gGY + bHg + 2, gSz, fontBold, C.azul);
    }

    const eStr = Math.round(eco).toString();
    const eSz  = 6;
    const eW   = fontBold.widthOfTextAtSize(eStr, eSz);
    if (bHe > 14) {
      drawText(p3, eStr, bX + bGW + 2 + (bGW - eW) / 2, gGY + bHe - 11, eSz, fontBold, C.branco);
    } else {
      drawText(p3, eStr, bX + bGW + 2 + (bGW - eW) / 2, gGY + bHe + 2, eSz, fontBold, C.verde);
    }

    // Mês no eixo X centralizado, bold azul
    const mStr = mesesAbrev[i];
    const mW   = fontBold.widthOfTextAtSize(mStr, 7.5);
    drawText(p3, mStr, bX + (bGW * 2 + 2 - mW) / 2, gGY - 13, 7.5, fontBold, C.azul);
  });

  // Legenda — fica logo abaixo do eixo X
  const legY = gGY - eixoXH - legH + 4;
  drawRect(p3, gGX + gGW/2 - 70, legY, 10, 8, rgb(0.267, 0.447, 0.769));
  drawText(p3, 'Geração (kWh)', gGX + gGW/2 - 58, legY + 2, 8, fontRegular, C.preto);
  drawRect(p3, gGX + gGW/2 + 20, legY, 10, 8, rgb(0.439, 0.678, 0.278));
  drawText(p3, 'Economia (R$)', gGX + gGW/2 + 32, legY + 2, 8, fontRegular, C.preto);

  // Disclaimer — fica na parte inferior da página, acima da margem
  const discY = margemInf + discH;
  drawBorderedRect(p3, ML, margemInf, CW, discH - 4, C.azulClaro, C.bordaAzul);
  [
    'A estimativa de geração média apresentada foi calculada em um cenário ideal. Por se tratar de uma fonte',
    'intermitente e sensível à fatores externos, a geração pode sofrer variações positivas ou negativas em',
    'decorrência das características físicas do telhado, sombreamento e/ou posicionamento dos módulos.',
  ].forEach((l, i) => drawText(p3, l, ML+4, discY - 14 - i * 12, 8, fontBold, C.preto));

  // ============================================================
  // PÁGINA 4 — PRODUTOS + ANÁLISE FINANCEIRA
  // ============================================================
  const p4 = pdfDoc.addPage([W, H]);
  let y4 = H - 30;

  y4 = drawSectionHeader(p4, '3 | Detalhamento dos Produtos', y4, C.azul);
  y4 -= 6;

  [
    'A seguir, detalharemos as informações referentes aos equipamentos deste orçamento. Informamos que não',
    'trabalhamos com estoque físico, então a disponibilidade dos equipamentos pode sofrer alterações.',
    'Neste caso, eventuais alterações em potência e modelo dos equipamentos orçados serão previamente comunicadas.',
  ].forEach((l, i) => { drawText(p4, l, ML, y4, 8, i > 0 ? fontBold : fontRegular, C.preto); y4 -= 11; });
  y4 -= 6;

  const hW = CW / 2 - 4;

  // FIX: drawProdTable usando drawRow para alinhamento correto
  const drawProdTable = (page, titulo, rows, x, y, w) => {
    drawBorderedRect(page, x, y-tH+2, w, tH, C.azul, C.bordaAzul);
    drawText(page, titulo, x + w/2 - fontBold.widthOfTextAtSize(titulo, 8)/2, y-tH+5, 8, fontBold, C.branco);
    let cy = y - tH;
    rows.forEach(([label, val], ri) => {
      const fill = ri % 2 === 0 ? C.azulClaro : C.azulMedio;
      cy = drawRow(page, [
        { x,             w: w*0.52, fill, text: label, font: fontBold,    color: C.azul  },
        { x: x+w*0.52,   w: w*0.48, fill, text: val,   font: fontRegular, color: C.preto },
      ], cy);
    });
    return cy;
  };

  const y4StartTbls = y4;
  const y4AfterMod = drawProdTable(p4, 'Módulo Fotovoltaico', [
    ['Potência:',          `${potModulo} W`],
    ['Modelo:',            proposta.kit.modulo?.modelo || ''],
    ['Fabricante:',        proposta.kit.modulo?.marca  || ''],
    ['Garantia (Geração):', `${proposta.kit.modulo?.garantiaeficiencia || ''} Anos`],
    ['Garantia (Fábrica):', `${proposta.kit.modulo?.garantiafabrica    || ''} Anos`],
  ], ML, y4StartTbls, hW);

  const y4AfterInv = drawProdTable(p4, 'Inversor', [
    ['Potência:',   `${potInversor} kW`],
    ['Modelo:',     proposta.kit.inversor?.modelo     || ''],
    ['Fabricante:', proposta.kit.inversor?.fabricante || ''],
    ['Garantia:',   `${proposta.kit.inversor?.garantia || ''} Anos`],
    ['Tensão:',     `${proposta.kit.inversor?.tensao   || ''} V`],
  ], ML+hW+8, y4StartTbls, hW);

  const yAfterTbls = Math.min(y4AfterMod, y4AfterInv) - 4;
  const imgH = 80;

  if (moduloImg) {
    const d = moduloImg.scaleToFit(hW - 10, imgH);
    p4.drawImage(moduloImg, { x: ML + (hW - d.width)/2, y: yAfterTbls - imgH, width: d.width, height: d.height });
  }
  if (inversorImg) {
    const d = inversorImg.scaleToFit(hW - 10, imgH);
    p4.drawImage(inversorImg, { x: ML+hW+8 + (hW - d.width)/2, y: yAfterTbls - imgH, width: d.width, height: d.height });
  }

  let y4b = yAfterTbls - imgH - 10;
  drawText(p4, '*imagens meramente ilustrativas', ML, y4b, 7, fontRegular, C.cinza);
  y4b -= 14;

  const y4AfterEst = drawProdTable(p4, 'Estrutura de Fixação', [
    ['Superfície',           proposta.superficie?.nome || 'Fibrocimento'],
    ['Garantia:',            '15 anos'],
    ['Material:',            'Aço INOX'],
    ['Peso Total Estimado:', `${pesoTotal} kg`],
  ], ML, y4b, hW);

  if (estruturaImg) {
    const d = estruturaImg.scaleToFit(hW - 10, 90);
    p4.drawImage(estruturaImg, { x: ML+hW+8 + (hW - d.width)/2, y: y4b - 90, width: d.width, height: d.height });
  }

  drawText(p4, 'Obs.: Estrutura não contempla correção de inclinação.', ML, y4AfterEst - 6, 7.5, fontBold, C.preto);
  let y4c = Math.min(y4AfterEst, y4b - 96) - 14;

  drawLine(p4, ML, y4c+6, ML+CW, y4c+6, C.bordaAzul, 0.5);
  y4c -= 4;
  y4c = drawSectionHeader(p4, '4 | Análise Financeira', y4c, C.azul);
  y4c -= 6;

  [
    'Nas páginas anteriores, ilustramos o valor que foi pago em energia elétrica nos últimos 5 anos.',
    'Agora, vamos verificar o quanto você economizará nos próximos 5!',
    'E aí, já pensou no que fazer com esta economia?',
  ].forEach((l) => { drawText(p4, l, ML, y4c, 8, fontBold, C.preto); y4c -= 11; });
  y4c -= 4;

  const afC = [CW*0.10, CW*0.18, CW*0.20, CW*0.26, CW*0.26];
  const afX = [ML, ML+afC[0], ML+afC[0]+afC[1], ML+afC[0]+afC[1]+afC[2], ML+afC[0]+afC[1]+afC[2]+afC[3]];

  y4c = drawRow(p4, ['Ano','Consumo Total','Tarifa de Energia','Economia Anual','Economia Anual (%)'].map((h, i) => ({
    x: afX[i], w: afC[i], fill: C.azulMedio, text: h, font: fontBold, color: C.preto,
  })), y4c);

  analise.forEach((row, ri) => {
    const fill = ri % 2 === 0 ? C.branco : C.cinzaClaro;
    y4c = drawRow(p4, [
      { x: afX[0], w: afC[0], fill: C.azul, text: String(row.ano),            font: fontBold,    color: C.branco },
      { x: afX[1], w: afC[1], fill,          text: String(row.consumo),        font: fontRegular, color: C.preto  },
      { x: afX[2], w: afC[2], fill,          text: `R$ ${row.tarifa.toFixed(2)}`, font: fontRegular, color: C.preto },
      { x: afX[3], w: afC[3], fill,          text: brl(row.econAnual),         font: fontBold,    color: C.verde  },
      { x: afX[4], w: afC[4], fill,          text: pct(row.econPct),           font: fontBold,    color: C.verde  },
    ], y4c);
  });
  y4c -= 6;

  [
    ['Premissas do estudo:', true],
    ['Não houve variação no consumo anual', false],
    ['IPCA + Delta Tarifário: + 5% a.a. (estimativa com base nos últimos 15 anos)', false],
    ['Considerando um consumo instantâneo de 40%', false],
  ].forEach(([l, bold]) => {
    drawText(p4, l, ML, y4c, 8, bold ? fontBold : fontRegular, C.preto);
    y4c -= 11;
  });

  // ============================================================
  // PÁGINA 5 — SIMULAÇÃO + RESUMO + VALOR
  // ============================================================
  const p5 = pdfDoc.addPage([W, H]);
  let y5 = H - 30;

  // Simulação de Financiamento
  drawBorderedRect(p5, ML, y5-tH+2, CW, tH, C.azul, C.bordaAzul);
  drawText(p5, 'Simulação de Financiamento', cx('Simulação de Financiamento', 9, fontBold), y5-tH+5, 9, fontBold, C.branco);
  y5 -= tH;

  y5 = drawRow(p5, [
    { x: ML,          w: CW*0.60, fill: C.azulMedio, text: 'Resumo',    font: fontBold, color: C.preto },
    { x: ML+CW*0.60,  w: CW*0.40, fill: C.azulMedio, text: 'Quantidade', font: fontBold, color: C.preto },
  ], y5);

  [
    ['Prazo de Carência Sugerido:', '90 dias corridos'],
    ['Simulação em 36x', `R$ ${calcParcela(valorProjeto, 36).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
    ['Simulação em 48x', `R$ ${calcParcela(valorProjeto, 48).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
    ['Simulação em 60x', `R$ ${calcParcela(valorProjeto, 60).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
    ['Simulação em 72x', `R$ ${calcParcela(valorProjeto, 72).toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}`],
  ].forEach(([l, v], ri) => {
    const fill = ri % 2 === 0 ? C.branco : C.cinzaClaro;
    y5 = drawRow(p5, [
      { x: ML,         w: CW*0.60, fill, text: l, font: fontRegular, color: C.preto },
      { x: ML+CW*0.60, w: CW*0.40, fill, text: v, font: fontBold,    color: C.verde, align: 'right' },
    ], y5);
  });

  drawText(p5, '*Exemplo de simulação considerando a taxa de juros de 1,85% a.m. IOF e Tarifa de cadastro inclusos', ML, y5-4, 6.5, fontRegular, C.cinza);
  y5 -= 12;
  drawText(p5, '**Sujeito a aprovação de crédito', ML, y5-4, 6.5, fontRegular, C.cinza);
  y5 -= 20;

  // Payback + Valor Investimento lado a lado
  const pbW  = CW * 0.52;
  const invW2 = CW * 0.44;
  const invX  = ML + pbW + 8;
  const y5Inv = y5;

  const pbRows = [
    ['Payback Recurso Próprio:',  `${paybackProprio.toFixed(2)}`,        'anos'],
    ['Payback Financiamento:',    `${paybackFinanciamento.toFixed(2)}`,   'anos'],
    ['Vida útil do Sistema:',     '30',                                   'anos'],
    ['ROI:',                      `${roi.toFixed(2)}`,                    ''],
    ['Retorno Mensal:',           `${(retornoMensal*100).toFixed(2)}%`,   'a.m.'],
  ];
  pbRows.forEach(([label, val, unit], ri) => {
    const fill = ri % 2 === 0 ? C.azulClaro : C.azulMedio;
    y5 = drawRow(p5, [
      { x: ML,              w: pbW*0.60, fill, text: label, font: fontBold,    color: C.azul  },
      { x: ML+pbW*0.60,     w: pbW*0.20, fill, text: val,   font: fontBold,    color: C.verde, align: 'right' },
      { x: ML+pbW*0.80,     w: pbW*0.20, fill, text: unit,  font: fontRegular, color: C.cinza },
    ], y5);
  });

  // Box valor investimento
  drawBorderedRect(p5, invX, y5Inv-tH+2, invW2, tH, C.azul, C.bordaAzul);
  drawText(p5, 'Valor do Investimento:', invX + invW2/2 - fontBold.widthOfTextAtSize('Valor do Investimento:', 8)/2, y5Inv-tH+5, 8, fontBold, C.branco);
  const boxH = tH * 5;
  drawBorderedRect(p5, invX, y5Inv - boxH - tH + 2, invW2, boxH, C.azulClaro, C.bordaAzul);
  const invStr = valorProjeto.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  const invFull = `R$ ${invStr}`;
  drawText(p5, invFull, invX + (invW2 - fontBold.widthOfTextAtSize(invFull, 14)) / 2, y5Inv - tH*3 + 5, 14, fontBold, C.verde);

  y5 -= 14;

  // FIX: Seção "5 |" (era "8 |")
  y5 = drawSectionHeader(p5, '5 | Resumo e valor do investimento', y5, C.azul);
  y5 -= 8;

  // Tabela componentes
  y5 = drawRow(p5, [
    { x: ML,          w: CW*0.75, fill: C.azulMedio, text: 'Componentes', font: fontBold, color: C.preto },
    { x: ML+CW*0.75,  w: CW*0.25, fill: C.azulMedio, text: 'Quantidade',  font: fontBold, color: C.preto },
  ], y5);

  [
    ['Módulo Monocristalino:',                                                      String(qtdModulos)],
    ['Inversor:',                                                                   String(qtdInversores)],
    ['Estrutura Para Telhado:',                                                     String(Math.ceil(qtdModulos / 4))],
    ['Kit Material Elétrico (protetor de surto, cabos, disjuntor e conectores):',  '1'],
    ['Frete:',                                                                      'Incluso'],
    ['Serviço de Instalação, Projetos e Solicitação de Acesso de Rede:',           '1'],
    ['Acompanhamento Remoto e monitoramento:',                                      'Incluso'],
  ].forEach(([l, v], ri) => {
    const fill = ri % 2 === 0 ? C.branco : C.cinzaClaro;
    y5 = drawRow(p5, [
      { x: ML,          w: CW*0.75, fill, text: l, font: fontRegular, color: C.preto },
      { x: ML+CW*0.75,  w: CW*0.25, fill, text: v, font: fontRegular, color: C.preto },
    ], y5);
  });
  y5 -= 8;

  // Valor total do projeto
  drawBorderedRect(p5, ML, y5-tH+2, CW, tH+2, C.azulClaro, C.bordaAzul);
  drawText(p5, 'Valor total do Projeto:', ML+6, y5-tH+6, 10, fontBold, C.azul);
  const vtStr = valorProjeto.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  drawText(p5, `R$ ${vtStr}`, ML+CW - fontBold.widthOfTextAtSize(`R$ ${vtStr}`, 11) - 6, y5-tH+5, 11, fontBold, C.azul);
  y5 -= tH + 6;

  // FIX: Economia mensal sem "R$ R$"
  drawBorderedRect(p5, ML, y5-tH+2, CW, tH+2, C.azulClaro, C.bordaAzul);
  drawText(p5, 'Economia Mensal:', ML+6, y5-tH+6, 10, fontBold, C.azul);
  const ecStr = economiaMensal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  drawText(p5, `R$ ${ecStr}`, ML+CW - fontBold.widthOfTextAtSize(`R$ ${ecStr}`, 11) - 6, y5-tH+5, 11, fontBold, C.azul);
  y5 -= tH + 10;

  // Prazo e validade
  drawBorderedRect(p5, ML, y5-tH+2, CW, tH, C.azulMedio, C.bordaAzul);
  drawText(p5, 'Prazo de Entrega, validade da proposta e observações:', cx('Prazo de Entrega, validade da proposta e observações:', 8, fontBold), y5-tH+5, 8, fontBold, C.azul);
  y5 -= tH + 4;
  drawText(p5, 'Prazo estimado: 90 dias após confirmação do pagamento e assinatura do contrato', ML, y5, 8, fontBold, C.preto);
  y5 -= 12;
  drawText(p5, 'Para mais formas de pagamento, consulte nossa equipe', ML, y5, 8, fontBold, C.preto);
  y5 -= 14;
  drawBorderedRect(p5, ML, y5-tH+2, CW, tH, C.azulMedio, C.bordaAzul);
  drawText(p5, 'Proposta Válida por 10 dias', cx('Proposta Válida por 10 dias', 9, fontBold), y5-tH+5, 9, fontBold, C.azul);
  y5 -= 40;

  // Rodapé
  drawLine(p5, ML, y5+10, ML+CW*0.44, y5+10, C.preto, 0.8);
  drawLine(p5, ML+CW*0.56, y5+10, ML+CW, y5+10, C.preto, 0.8);
  drawText(p5, empresa?.razaoSocial || 'Marcki Engenharia LTDA', ML, y5, 8, fontBold,    C.preto);
  drawText(p5, `CNPJ: ${empresa?.cnpj || '38.143.553/0001-76'}`, ML, y5-11, 8, fontRegular, C.preto);
  drawText(p5, `Cliente: ${proposta.cliente?.nome || ''}`, ML+CW*0.56, y5, 8, fontRegular, C.preto);
  drawText(p5, `CPF/CNPJ: ${proposta.cliente?.cpf || ''}`, ML+CW*0.56, y5-11, 8, fontRegular, C.preto);


  // ============================================================
  // PÁGINA 6 — PRODUÇÃO ANUAL 25 ANOS
  // ============================================================
  const p6 = pdfDoc.addPage([W, H]);
  let y6 = H - 30;

  y6 = drawSectionHeader(p6, '6 | Produção Anual — 25 Anos', y6, C.azul);
  y6 -= 8;

  // Texto introdutório
  [
    'O gráfico abaixo apresenta a estimativa de produção anual do sistema ao longo de 25 anos,',
    'considerando a depreciação natural dos módulos fotovoltaicos ao longo do tempo.',
  ].forEach((l) => { drawText(p6, l, ML, y6, 8, fontRegular, C.preto); y6 -= 11; });
  y6 -= 6;

  // ── Calcular dados anuais ──
  const anoBase = new Date().getFullYear();
  const hspMediaAnual = hspData?.hspMedia || (hspMes.reduce((a, b) => a + b, 0) / 12);
  const geracaoAno0 = potCC * hspMediaAnual * 30 * pr * fatorOrientacao * 12;

  const dadosAnuais = Array.from({ length: 26 }, (_, i) => {
    let producao;
    if (i === 0)      producao = geracaoAno0;
    else if (i === 1) producao = geracaoAno0 * (1 - deprecPrimeiro);
    else              producao = geracaoAno0 * (1 - deprecPrimeiro) * ((1 - deprecDemais) ** (i - 1));
    return { ano: anoBase + i, producao: Math.round(producao) };
  });

  const maxAnual = Math.max(...dadosAnuais.map((d) => d.producao)) * 1.12;
  const totalProducao25 = dadosAnuais.reduce((s, d) => s + d.producao, 0);

  // ── GRÁFICO — metade superior da página ──
  const gAX  = ML + 44;
  const gAW  = CW - 48;
  const gAH  = 220;                  // altura fixa — metade da página
  const gAY  = y6 - gAH - 28;       // base do gráfico
  const slotA = gAW / 26;
  const barAW = slotA * 0.68;

  // Título
  const tAnual = `Produção Anual (kWh) — Total 25 anos: ${Math.round(totalProducao25).toLocaleString('pt-BR')} kWh`;
  drawText(p6, tAnual, cx(tAnual, 9, fontBold), y6 - 2, 9, fontBold, C.preto);

  // Eixos
  drawLine(p6, gAX, gAY, gAX, gAY + gAH, C.cinza, 0.8);
  drawLine(p6, gAX, gAY, gAX + gAW, gAY, C.cinza, 0.8);

  // Grade e eixo Y
  for (let i = 0; i <= 5; i += 1) {
    const val = Math.round(maxAnual * i / 5);
    const yp  = gAY + (gAH * i / 5);
    drawLine(p6, gAX, yp, gAX + gAW, yp, rgb(0.85, 0.85, 0.85), 0.3);
    const label = `${(val / 1000).toFixed(1)}k`;
    drawText(p6, label, ML, yp - 3, 6.5, fontRegular, C.cinza);
  }

  // Barras + rótulos
  dadosAnuais.forEach((d, i) => {
    const bH  = (d.producao / maxAnual) * gAH;
    const bX  = gAX + i * slotA + (slotA - barAW) / 2;
    const fat = 1 - (i / 25) * 0.3;
    drawRect(p6, bX, gAY, barAW, bH, rgb(0.102 * fat + 0.5 * (1-fat), 0.447 * fat + 0.6 * (1-fat), 0.769));

    // Ano no eixo X (alternado)
    if (i % 2 === 0) {
      const anoStr = String(d.ano);
      const aW = fontBold.widthOfTextAtSize(anoStr, 6);
      drawText(p6, anoStr, bX + (barAW - aW) / 2, gAY - 11, 6, fontBold, C.azul);
    }

    // Valor dentro da barra
    if (bH > 16) {
      const vStr = `${(d.producao / 1000).toFixed(1)}k`;
      const vW   = fontBold.widthOfTextAtSize(vStr, 5.5);
      drawText(p6, vStr, bX + (barAW - vW) / 2, gAY + bH - 11, 5.5, fontBold, C.branco);
    }
  });

  // ── TABELA — metade inferior da página ──
  // 4 colunas: Ano | Geração | Ano | Geração
  // 13 linhas à esquerda (anos 0-12) e 13 à direita (anos 13-25)
  const tblY     = gAY - 22;        // topo da tabela (logo abaixo dos anos do eixo X)
  const tblTH    = 13;              // altura da linha
  const colW     = CW / 4;         // largura de cada coluna
  const col1X    = ML;
  const col2X    = ML + colW;
  const col3X    = ML + colW * 2;
  const col4X    = ML + colW * 3;

  // Cabeçalho
  [
    { x: col1X, text: 'Ano',          bold: true },
    { x: col2X, text: 'Produção (kWh)', bold: true },
    { x: col3X, text: 'Ano',          bold: true },
    { x: col4X, text: 'Produção (kWh)', bold: true },
  ].forEach(({ x, text, bold }) => {
    drawBorderedRect(p6, x, tblY - tblTH + 2, colW, tblTH, C.azul, C.bordaAzul);
    drawText(p6, text, x + colW/2 - (bold ? fontBold : fontRegular).widthOfTextAtSize(text, 8)/2, tblY - tblTH + 5, 8, fontBold, C.branco);
  });

  // Linhas de dados — 13 pares lado a lado
  const metade = Math.ceil(dadosAnuais.length / 2); // 13
  for (let i = 0; i < metade; i += 1) {
    const rowY   = tblY - tblTH * (i + 2) + 2;
    const fill   = i % 2 === 0 ? C.azulClaro : C.branco;
    const dEsq   = dadosAnuais[i];
    const dDir   = dadosAnuais[i + metade];

    // Esquerda
    drawBorderedRect(p6, col1X, rowY, colW, tblTH, fill, C.bordaAzul);
    drawBorderedRect(p6, col2X, rowY, colW, tblTH, fill, C.bordaAzul);
    drawText(p6, String(dEsq.ano), col1X + colW/2 - fontBold.widthOfTextAtSize(String(dEsq.ano), 8)/2, rowY + 3, 8, fontBold, C.azul);
    const geEsq = dEsq.producao.toLocaleString('pt-BR');
    drawText(p6, geEsq, col2X + colW - fontRegular.widthOfTextAtSize(geEsq, 8) - 6, rowY + 3, 8, fontRegular, C.preto);

    // Direita
    if (dDir) {
      drawBorderedRect(p6, col3X, rowY, colW, tblTH, fill, C.bordaAzul);
      drawBorderedRect(p6, col4X, rowY, colW, tblTH, fill, C.bordaAzul);
      drawText(p6, String(dDir.ano), col3X + colW/2 - fontBold.widthOfTextAtSize(String(dDir.ano), 8)/2, rowY + 3, 8, fontBold, C.azul);
      const geDir = dDir.producao.toLocaleString('pt-BR');
      drawText(p6, geDir, col4X + colW - fontRegular.widthOfTextAtSize(geDir, 8) - 6, rowY + 3, 8, fontRegular, C.preto);
    }
  }

  // Linha total
  const totalRowY = tblY - tblTH * (metade + 2) + 2;
  drawBorderedRect(p6, col1X, totalRowY, CW / 2, tblTH, C.azulMedio, C.bordaAzul);
  drawBorderedRect(p6, col3X, totalRowY, CW / 2, tblTH, C.azulMedio, C.bordaAzul);
  drawText(p6, 'Total 25 anos:', col1X + 4, totalRowY + 3, 8, fontBold, C.azul);
  const totStr = `${Math.round(totalProducao25).toLocaleString('pt-BR')} kWh`;
  drawText(p6, totStr, col2X + colW - fontBold.widthOfTextAtSize(totStr, 8) - 6, totalRowY + 3, 8, fontBold, C.verde);
  drawText(p6, 'Total 25 anos:', col3X + 4, totalRowY + 3, 8, fontBold, C.azul);
  drawText(p6, totStr, col4X + colW - fontBold.widthOfTextAtSize(totStr, 8) - 6, totalRowY + 3, 8, fontBold, C.verde);

  // Disclaimer
  const discY6 = totalRowY - 10;
  drawBorderedRect(p6, ML, discY6 - 34, CW, 32, C.azulClaro, C.bordaAzul);
  [
    `* Estimativa baseada em dados solarimétricos PVGIS para ${proposta.localinstalacao?.cidade?.nome || ''}/${proposta.localinstalacao?.estado?.sigla || ''}.`,
    `  Depreciação: ${(deprecPrimeiro*100).toFixed(1)}% no 1º ano, ${(deprecDemais*100).toFixed(1)}% nos demais. PR: ${(pr*100).toFixed(0)}%. Fator Orientação: ${fatorOrientacao.toFixed(2)}.`,
    '  Valores reais podem variar conforme sombreamento, temperatura e condições locais de instalação.',
  ].forEach((l, i) => drawText(p6, l, ML + 4, discY6 - 8 - i * 10, 7, fontRegular, C.cinza));

  // ── Export ──
  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type: 'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = `Proposta_${(proposta.cliente?.nome || 'Cliente').replace(/ /g, '_')}_Marcki.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdf;
